/* Movimiento compartido: entradas finitas, FLIP y selección. Sin dependencias. */
(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;
  const ease = 'cubic-bezier(.23,1,.32,1)';
  const running = new Set(), channels = new WeakMap();
  let keyboard = false;
  const allowed = event => typeof Element.prototype.animate === 'function' && !reduced.matches && !keyboard && (!event || event.detail !== 0);
  function stop(element, channel = 'enter') {
    const animation = channels.get(element)?.get(channel);
    if (animation) { channels.get(element).delete(channel); animation.cancel(); }
  }
  function play(element, frames, options = {}, channel = 'enter') {
    stop(element, channel);
    if (!element.animate) return null;
    const animation = element.animate(frames, { duration: 240, easing: ease, ...options });
    if (!channels.has(element)) channels.set(element, new Map());
    channels.get(element).set(channel, animation);
    running.add(animation);
    const clean = () => {
      running.delete(animation);
      if (options.fill !== 'forwards' && channels.get(element)?.get(channel) === animation) channels.get(element).delete(channel);
    };
    animation.finished.then(clean, clean);
    return animation;
  }
  function finish() {
    [...running].forEach(animation => { try { animation.finish(); } catch (_) { animation.cancel(); } });
  }
  document.addEventListener('keydown', event => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    keyboard = true; root.dataset.motionInput = 'keyboard'; finish();
  }, true);
  document.addEventListener('pointerdown', () => {
    keyboard = false; delete root.dataset.motionInput;
  }, true);
  reduced.addEventListener('change', () => { if (reduced.matches) finish(); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) finish(); });
  window.addEventListener('hashchange', finish);

  function enter(elements, event, options = {}) {
    const list = [...elements];
    if (!allowed(event)) { list.forEach(el => stop(el)); return; }
    list.forEach((element, i) => {
      if (element.closest('[hidden]')) return;
      const previous = channels.get(element)?.get('enter');
      const style = previous ? getComputedStyle(element) : null;
      play(element, [
        { opacity: style ? style.opacity : 0, transform: style ? style.transform : 'translateY(10px)' },
        { opacity: 1, transform: 'none' }
      ], { duration: options.duration || 240, delay: (options.delay || 0) + Math.min(i, 3) * (options.stagger ?? 35), fill: 'backwards' });
    });
  }

  // Mide solo al cambiar el estado; el recorrido entre posiciones usa transform.
  function flip(elements, mutate, event, duration = 220) {
    const list = [...new Set(elements)].filter(el => !el.hidden);
    const animate = allowed(event);
    const before = animate ? list.map(el => el.getBoundingClientRect()) : [];
    list.forEach(el => stop(el, 'layout'));
    mutate();
    if (!animate) return;
    const after = list.map(el => el.getBoundingClientRect());
    list.forEach((el, i) => {
      const a = before[i], b = after[i], x = a.left - b.left, y = a.top - b.top;
      if (!b.width || (!x && !y) || (a.top > innerHeight && b.top > innerHeight) || (a.bottom < 0 && b.bottom < 0)) return;
      play(el, [{ transform: 'translate(' + x + 'px,' + y + 'px)' }, { transform: 'none' }], { duration }, 'layout');
    });
  }
  window.HotelMotion = { allowed, enter, flip, play, stop, finish, reduced, ease };

  document.addEventListener('DOMContentLoaded', () => {
    // Observamos cada pieza una sola vez. Volver atrás no oculta lo ya leído.
    const targets = document.querySelectorAll('.rv-card, .rv-steps li, .home-map-fact, .itin-note');
    const reveal = (element, index) => {
      element.classList.add('motion-seen');
      if (element.matches('.rv-card, .rv-steps li')) {
        // Una pieza completa: número, título y texto conservan su relación.
        // El orden de la fila guía la lectura; al volver no se oculta de nuevo.
        // Se cuenta sólo entre piezas iguales: el encabezado y la costura de
        // la banda de tarjetas de Reservas no corren el escalonado.
        const order = [...element.parentElement.children]
          .filter(hermano => hermano.matches('.rv-card, .rv-steps li')).indexOf(element);
        if (allowed()) play(element, [
          { opacity: 0, transform: 'translateY(8px)' },
          { opacity: 1, transform: 'none' }
        ], { duration: 320, delay: Math.min(order, 3) * 50, fill: 'backwards' });
      } else {
        enter([element], undefined, { duration: 360, delay: Math.min(index, 3) * 40 });
      }
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.filter(entry => entry.isIntersecting).forEach((entry, index) => {
          reveal(entry.target, index); observer.unobserve(entry.target);
        });
      }, { threshold: .16 });
      targets.forEach(el => observer.observe(el));
    } else targets.forEach(el => el.classList.add('motion-seen'));

    // Una línea continua conserva la referencia entre categorías, incluso
    // cuando las pestañas ocupan varias filas en móvil.
    document.querySelectorAll('.rooms-selector, .gal-filtros').forEach(group => {
      const buttons = [...group.querySelectorAll('button')];
      const indicator = document.createElement('span');
      indicator.className = 'motion-indicator'; indicator.setAttribute('aria-hidden', 'true');
      group.appendChild(indicator); group.classList.add('motion-tabs');
      let box, frame = 0;
      function update() {
        frame = 0;
        const button = buttons.find(b => b.getAttribute('aria-selected') === 'true' || b.getAttribute('aria-pressed') === 'true');
        if (!button || !group.offsetWidth) { box = null; return; }
        const g = group.getBoundingClientRect(), b = button.getBoundingClientRect();
        const next = { x: b.left - g.left + group.scrollLeft, y: b.bottom - g.top + group.scrollTop - 1, w: b.width };
        const old = indicator.getBoundingClientRect();
        stop(indicator, 'indicator');
        indicator.style.cssText = 'left:' + next.x + 'px;top:' + next.y + 'px;width:' + next.w + 'px';
        if (box && allowed() && next.w && (box.x !== next.x || box.y !== next.y || box.w !== next.w)) {
          const now = indicator.getBoundingClientRect();
          play(indicator, [
            { transform: 'translate(' + (old.left - now.left) + 'px,' + (old.top - now.top) + 'px) scaleX(' + old.width / next.w + ')' },
            { transform: 'none' }
          ], { duration: 220 }, 'indicator');
        }
        box = next;
      }
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const changes = new MutationObserver(schedule);
      buttons.forEach(button => changes.observe(button, { attributes: true, attributeFilter: ['aria-selected', 'aria-pressed'] }));
      const page = group.closest('.page');
      if (page) changes.observe(page, { attributes: true, attributeFilter: ['hidden'] });
      if ('ResizeObserver' in window) {
        const sizes = new ResizeObserver(() => { box = null; schedule(); });
        sizes.observe(group); buttons.forEach(b => sizes.observe(b));
      }
      document.addEventListener('i18n:change', () => { box = null; schedule(); });
      document.fonts?.ready.then(schedule);
      schedule();
    });

    document.addEventListener('click', event => {
      if (!event.target.closest('.hab-filtro')) return;
      enter(document.querySelectorAll('.hab-tipo:not([hidden]) .hab-tipo-details'), event);
    });
  });
})();
